import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X } from 'lucide-react';
import { fetchRecentSheets, forgetRecentSheet } from '@/lib/api/procurementImport';
import type { RecentSheet } from '@/types/procurementImport';

interface SourceStepProps {
  weekLabel: string;
  busy: boolean;
  error: string | null;
  onReadFile: (file: File) => void;
  onReadUrl: (url: string) => void;
  onDownloadTemplate: () => void;
}

const NOT_XLSX = 'Only .xlsx files can be imported';

export function SourceStep({ weekLabel, busy, error, onReadFile, onReadUrl, onDownloadTemplate }: SourceStepProps) {
  const [file, setFile] = useState<File | null>(null);
  const [url, setUrl] = useState('');
  const [fileError, setFileError] = useState<string | null>(null);
  const [recent, setRecent] = useState<RecentSheet[]>([]);
  const [recentError, setRecentError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const urlId = useId();

  useEffect(() => {
    let active = true;
    fetchRecentSheets().then((sheets) => active && setRecent(sheets)).catch(() => {});
    return () => { active = false; };
  }, []);

  const hasUrl = url.trim() !== '';
  const fileLocked = hasUrl;
  const urlLocked = file !== null;
  const canRead = (file !== null) !== hasUrl;

  const forget = (sheet: RecentSheet) => {
    const index = recent.findIndex((r) => r.sheetId === sheet.sheetId);
    setRecentError(null);
    setRecent((current) => current.filter((r) => r.sheetId !== sheet.sheetId));
    forgetRecentSheet(sheet.sheetId).catch(() => {
      // Put the chip back where it was.
      setRecent((current) => (current.some((r) => r.sheetId === sheet.sheetId) ? current : [...current.slice(0, index), sheet, ...current.slice(index)]));
      setRecentError("Couldn't remove that sheet; try again.");
    });
  };

  const choose = (candidate: File | undefined) => {
    if (!candidate) return;
    if (!/\.xlsx$/i.test(candidate.name)) {
      setFileError(NOT_XLSX);
      return;
    }
    setFileError(null);
    setFile(candidate);
  };

  const remove = () => {
    setFile(null);
    setFileError(null);
    if (fileInput.current) fileInput.current.value = '';
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Importing into the week of {weekLabel}</p>

      <div
        data-testid="drop-zone"
        onDragOver={(e) => { e.preventDefault(); if (!fileLocked) setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (!fileLocked && !busy) choose(e.dataTransfer?.files?.[0]);
        }}
        className={`flex flex-col items-start gap-3 rounded-lg border-2 border-dashed p-5 ${dragging ? 'border-primary bg-primary/5' : 'border-border'} ${fileLocked ? 'opacity-50' : ''}`}
      >
        {file ? (
          <div className="flex items-center gap-3 text-sm">
            <span className="font-medium">{file.name}</span>
            <button type="button" className="text-xs underline underline-offset-4" onClick={remove} disabled={busy}>Remove</button>
          </div>
        ) : (
          <>
            <p className="text-sm">Drag and drop your Excel file here</p>
            <Button type="button" disabled={fileLocked || busy} onClick={() => fileInput.current?.click()}>Upload file</Button>
          </>
        )}
        <input
          ref={fileInput}
          type="file"
          accept=".xlsx"
          aria-label="Excel file (.xlsx)"
          className="hidden"
          disabled={fileLocked || busy}
          onChange={(e) => choose(e.target.files?.[0])}
        />
        {fileError && <p className="text-xs text-destructive">{fileError}</p>}
      </div>

      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <div className="h-px flex-1 bg-border" />
        <span>or</span>
        <div className="h-px flex-1 bg-border" />
      </div>

      <div className={`space-y-2 ${urlLocked ? 'opacity-50' : ''}`}>
        <label htmlFor={urlId} className="text-sm font-medium">Google Sheets link</label>
        <Input id={urlId} type="url" placeholder="Paste the link from Share…" value={url} disabled={urlLocked || busy} onChange={(e) => setUrl(e.target.value)} />
        {recent.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">Recent sheets</span>
            {recent.map((sheet) => (
              <span key={sheet.sheetId} className="inline-flex items-center rounded-md border border-input">
                <Button type="button" variant="ghost" size="sm" className="rounded-r-none" title={sheet.url} disabled={urlLocked || busy} onClick={() => setUrl(sheet.url)}>
                  {sheet.title}
                </Button>
                <Button type="button" variant="ghost" size="icon" className="h-6 w-6 rounded-l-none" aria-label={`Remove ${sheet.title} from recent sheets`} disabled={busy} onClick={() => forget(sheet)}>
                  <X className="h-3 w-3" />
                </Button>
              </span>
            ))}
          </div>
        )}
        {recentError && <p className="text-xs text-destructive">{recentError}</p>}
      </div>

      {error && <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">{error}</p>}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <button type="button" className="text-sm underline underline-offset-4" onClick={onDownloadTemplate} disabled={busy}>
          Download the template
        </button>
        <Button disabled={!canRead || busy} onClick={() => (file ? onReadFile(file) : onReadUrl(url.trim()))}>
          {busy ? 'Reading…' : 'Read sheet'}
        </Button>
      </div>
    </div>
  );
}
